// FUNCTIONALITY-115C — theme preference: system | light | dark.
export type StoreV2Theme = "system" | "light" | "dark";

const THEME_KEY = "asiastyle.store-v2.theme.v1";

export function readStoreV2Theme(): StoreV2Theme {
  try {
    const raw = localStorage.getItem(THEME_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    /* ignore */
  }
  return "system";
}

export function writeStoreV2Theme(theme: StoreV2Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* ignore */
  }
}

function telegramColorScheme(): "light" | "dark" | null {
  try {
    const scheme = (
      window as unknown as { Telegram?: { WebApp?: { colorScheme?: string } } }
    ).Telegram?.WebApp?.colorScheme;
    if (scheme === "dark" || scheme === "light") return scheme;
  } catch {
    /* ignore */
  }
  return null;
}

function prefersDark(): boolean {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

export function resolveEffectiveTheme(preference: StoreV2Theme): "light" | "dark" {
  if (preference === "light" || preference === "dark") return preference;
  return telegramColorScheme() ?? (prefersDark() ? "dark" : "light");
}

/** Apply theme to document + store scroll root. Preserves checkout state (no remount). */
export function applyStoreV2Theme(preference: StoreV2Theme): "light" | "dark" {
  const effective = resolveEffectiveTheme(preference);
  try {
    document.documentElement.dataset.sv2Theme = preference;
    document.documentElement.dataset.sv2ThemeEffective = effective;
    const root = document.querySelector<HTMLElement>("[data-store-scroll-root='true'], .sv2-root");
    if (root) {
      root.dataset.sv2Theme = preference;
      root.dataset.sv2ThemeEffective = effective;
    }
  } catch {
    /* ignore */
  }
  return effective;
}

export function cycleStoreV2Theme(current: StoreV2Theme): StoreV2Theme {
  if (current === "system") return "light";
  if (current === "light") return "dark";
  return "system";
}

export function themeLabel(lang: "ua" | "en", theme: StoreV2Theme): string {
  if (lang === "en") {
    if (theme === "system") return "Theme: Auto";
    if (theme === "light") return "Theme: Light";
    return "Theme: Dark";
  }
  if (theme === "system") return "Тема: Авто";
  if (theme === "light") return "Тема: Світла";
  return "Тема: Темна";
}

/** Eager apply so consent gate inherits resolved theme before first paint when possible. */
try {
  if (typeof document !== "undefined") {
    applyStoreV2Theme(readStoreV2Theme());
  }
} catch {
  /* ignore */
}
